import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  InputBase,
  Tab,
  Tabs,
  Avatar,
  useTheme,
  Button,
  Skeleton,
  Paper,
  Pagination,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SortIcon from "@mui/icons-material/Sort";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import { ClientGetMyPackage } from "api/client";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import { useLocation } from "react-router-dom";
import FlexBetween from "components/FlexBetween";

const TABS = [
  { label: "All",                status: undefined },
  { label: "In-Transit",         status: "Inprogress" },
  { label: "At Hub",             status: "At Hub" },
  { label: "Arrived",            status: "Arrived" },
  { label: "Delivered",          status: "Delivered" },
  { label: "Cancelled & Others", status: "Cancelled" },
];

const STATUS_TO_TAB = {
  Started:      1,
  Pending:      1,
  "In-transit": 1,
  Inprogress:   1,
  "At Hub":     2,
  Arrived:      3,
  Delivered:    4,
  Cancelled:    5,
  Others:       5,
};

const PAGE_SIZE = 20;

export const displayStatus = (status) => {
  if (["Pending", "Inprogress", "In-progress", "In-transit"].includes(status)) return "In-Transit";
  if (status === "At Hub") return "At Hub";
  return status || "Unknown";
};

const getStatusConfig = (status, theme) => {
  switch (status) {
    case "Pending":
    case "Started":
      return {
        label:   "Pending",
        Icon:    HourglassEmptyIcon,
        color:   theme.palette.info.main,
        bgColor: "#e3f2fd",
      };
    case "Inprogress":
    case "In-progress":
    case "In-transit":
      return {
        label:   "In-Transit",
        Icon:    LocalShippingIcon,
        color:   theme.palette.warning.main,
        bgColor: "#fff8e1",
      };
    case "At Hub":
      return {
        label:   "At Hub",
        Icon:    SortIcon,
        color:   "#7b1fa2",
        bgColor: "#f3e5f5",
      };
    case "Arrived":
      return {
        label:   "Arrived",
        Icon:    StorefrontIcon,
        color:   theme.palette.secondary.main,
        bgColor: "#f3e5f5",
      };
    case "Delivered":
      return {
        label:   "Delivered",
        Icon:    CheckCircleIcon,
        color:   theme.palette.success.main,
        bgColor: "#f1f8e9",
      };
    case "Cancelled":
      return {
        label:   "Cancelled",
        Icon:    CancelIcon,
        color:   theme.palette.error.main,
        bgColor: "#fff0f0",
      };
    default:
      return {
        label:   status || "Unknown",
        Icon:    HelpOutlineIcon,
        color:   theme.palette.grey[600],
        bgColor: "#f5f5f5",
      };
  }
};

// --- Package Card ---
const PackageCard = ({ pkg, theme, onTrack }) => {
  const { label, Icon, color, bgColor } = getStatusConfig(pkg.status, theme);
  const fee = Number(pkg.shippingFee || 0) + Number(pkg.amount || 0);

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        border: `1px solid ${theme.palette.divider}`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.13)",
        },
      }}
    >
      {/* Status color strip */}
      <Box sx={{ height: 4, background: color, flexShrink: 0 }} />

      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: "8px !important", flexGrow: 1 }}>
        {/* Status chip + fee */}
        <FlexBetween mb={1.5}>
          <Chip
            label={label}
            size="small"
            sx={{
              bgcolor: bgColor,
              color,
              fontWeight: 700,
              fontSize: "0.65rem",
              height: 20,
              border: `1px solid ${color}40`,
              px: 0.25,
            }}
          />
          {fee > 0 && (
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.secondary.main, fontSize: "0.78rem" }}
            >
              ฿{fee.toLocaleString()}
            </Typography>
          )}
        </FlexBetween>

        {/* Status icon */}
        <Box display="flex" justifyContent="center" mb={1.5}>
          <Avatar
            sx={{
              width: { xs: 52, sm: 60 },
              height: { xs: 52, sm: 60 },
              bgcolor: bgColor,
              border: `2.5px solid ${color}45`,
            }}
          >
            <Icon sx={{ fontSize: { xs: 26, sm: 30 }, color }} />
          </Avatar>
        </Box>

        {/* Tracking number */}
        <Typography
          variant="body2"
          fontWeight={700}
          fontFamily="monospace"
          textAlign="center"
          noWrap
          sx={{
            letterSpacing: 0.3,
            mb: 0.5,
            fontSize: { xs: "0.72rem", sm: "0.82rem" },
          }}
        >
          {pkg.tracking}
        </Typography>

        {/* Description */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textAlign: "center",
            lineHeight: 1.4,
            mb: 0.75,
            minHeight: "2.8em",
            fontSize: "0.7rem",
          }}
        >
          {pkg.description || "—"}
        </Typography>

        {/* Station */}
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: "block", textAlign: "center", fontSize: "0.68rem" }}
        >
          📍 {pkg.stations?.name || pkg.station || "—"}
        </Typography>

        {/* Time */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", textAlign: "center", mt: 0.25, fontSize: "0.68rem" }}
        >
          {pkg.updatedAt ? moment(pkg.updatedAt).fromNow() : ""}
        </Typography>
      </CardContent>

      <CardActions sx={{ p: { xs: 1.5, sm: 2 }, pt: "6px !important" }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
          onClick={() => onTrack(pkg.tracking)}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
            borderColor: color,
            color,
            py: 0.5,
            "&:hover": { bgcolor: bgColor, borderColor: color },
          }}
        >
          Track
        </Button>
      </CardActions>
    </Card>
  );
};

// --- Skeleton card (loading state) ---
const SkeletonCard = () => (
  <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
    <Skeleton variant="rectangular" height={4} />
    <CardContent sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" mb={1.5}>
        <Skeleton variant="rounded" width={72} height={20} />
        <Skeleton variant="text" width={36} />
      </Box>
      <Box display="flex" justifyContent="center" mb={1.5}>
        <Skeleton variant="circular" width={56} height={56} />
      </Box>
      <Skeleton variant="text" sx={{ mx: 2 }} />
      <Skeleton variant="text" sx={{ mx: 2 }} />
      <Skeleton variant="text" sx={{ mx: 4 }} />
      <Skeleton variant="text" sx={{ mx: 5 }} />
    </CardContent>
    <Box sx={{ p: 2, pt: 0 }}>
      <Skeleton variant="rounded" height={30} sx={{ borderRadius: 2 }} />
    </Box>
  </Card>
);

// --- Main component ---
const MyParcels = () => {
  const theme = useTheme();
  const location = useLocation();

  const initialStatus = new URLSearchParams(location.search).get("status");
  const initialTab = STATUS_TO_TAB[initialStatus] ?? 0;

  // All filter state in one object to avoid double-fetch race conditions
  const [filters, setFilters] = useState({
    tab:    initialTab,
    search: "",
    page:   1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState("");

  const debounceRef = useRef();
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const fetchPackages = useCallback((pg, srch, status) => {
    setIsLoading(true);
    ClientGetMyPackage(
      pg - 1,
      PAGE_SIZE,
      { field: "updatedAt", sort: "desc" },
      srch,
      status
    )
      .then((res) => {
        if (res && res.status === 200) {
          setRows(res.packages || []);
          setTotalCount(res.total || 0);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchPackages(filters.page, filters.search, TABS[filters.tab]?.status);
    // eslint-disable-next-line
  }, [filters]);

  const handleTabChange = (_, v) => {
    setFilters({ tab: v, search: "", page: 1 });
    setSearchInput("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters((f) => ({ ...f, search: val, page: 1 }));
    }, 600);
  };

  const clearSearch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput("");
    setFilters((f) => ({ ...f, search: "", page: 1 }));
  };

  const handlePageChange = (_, p) => {
    setFilters((f) => ({ ...f, page: p }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight="600" color="secondary" sx={{ mb: 3 }}>
        My Parcels
      </Typography>

      {/* Search bar */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 0.75,
          mb: 2,
          border: `1.5px solid ${theme.palette.divider}`,
          borderRadius: 3,
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:focus-within": {
            borderColor: theme.palette.secondary.main,
            boxShadow: `0 0 0 3px ${theme.palette.secondary.main}18`,
          },
        }}
      >
        <Search sx={{ color: theme.palette.text.disabled, flexShrink: 0 }} />
        <InputBase
          placeholder="Search tracking number, description..."
          fullWidth
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ fontSize: "0.9rem" }}
        />
        {searchInput && (
          <IconButton size="small" onClick={clearSearch} sx={{ color: "text.secondary" }}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Paper>

      {/* Status tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={filters.tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minWidth: 60,
              fontSize: "0.85rem",
              padding: "0.5rem",
            },
            "& .Mui-selected": { color: `${theme.palette.secondary.main} !important` },
            "& .MuiTabs-indicator": { bgcolor: theme.palette.secondary.main },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.label} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Result count */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          {isLoading ? (
            <Skeleton variant="text" width={100} sx={{ display: "inline-block" }} />
          ) : (
            <>
              <b>{totalCount}</b> parcel{totalCount !== 1 ? "s" : ""}
              {filters.search && (
                <> matching "<b>{filters.search}</b>"</>
              )}
            </>
          )}
        </Typography>
      </Box>

      {/* Grid */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <HelpOutlineIcon
            sx={{ fontSize: 56, color: "text.disabled", opacity: 0.4, display: "block", mx: "auto", mb: 2 }}
          />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            {filters.search ? `No results for "${filters.search}"` : "No parcels found"}
          </Typography>
          <Typography variant="body2" color="text.disabled" mt={0.5}>
            {filters.search
              ? "Try a different search term"
              : "Your shipments will appear here"}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {rows.map((pkg) => (
              <Grid item xs={6} sm={4} md={3} key={pkg._id}>
                <PackageCard
                  pkg={pkg}
                  theme={theme}
                  onTrack={(tracking) => {
                    setSelectedTracking(tracking);
                    setTrackingOpen(true);
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={4} mb={2}>
              <Pagination
                count={pageCount}
                page={filters.page}
                onChange={handlePageChange}
                color="secondary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      <TrackingDetailDialog
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        trackingNumber={selectedTracking}
        hideEditHistory
      />
    </Box>
  );
};

export default MyParcels;
